import { FC, ReactElement, useId } from 'react'
import { useIntl } from 'react-intl'
import { toast } from 'sonner'
import { Copy, KeyRound, ShieldAlert } from 'lucide-react'
import type { PatchSession } from '../../../services/api/patchSession.api'
import { resolvePatchSessionView, type PatchSessionView } from './patchSessionState'
import { neutraliseBidi } from './textSafety'

interface Props {
  /**
   * Every live session the caller can reach, newest first — not just this
   * chat's. A session opened from another conversation is still usable from
   * here, so under-reporting it would hide work the agent can actually act on.
   */
  sessions: PatchSession[]
  /** Which row, if any, belongs to the chat currently open. */
  currentChatId: string
}

/**
 * Stable identity for the panel's live region across every state transition a
 * row can go through. A live region that is inserted at the same moment as
 * its own text is frequently not announced at all — the region has to already
 * exist for the change to read as a change (the same reasoning `ApprovalCard`
 * documents for its always-mounted status paragraph).
 */
const LIVE_REGION_KEY = 'patch-live-region'

/**
 * Whole minutes left, floored at zero.
 *
 * Never negative: `expires_at` in the past means the row has lapsed but has
 * not been read-and-settled yet, and "-3m left" reads as a bug. The service
 * settles a lapsed session the next time anything reads it, so this state is
 * brief and self-correcting — it just must not render as a negative duration.
 */
function minutesLeft(expiresAt: string): number {
  const remainingMs = new Date(expiresAt).getTime() - Date.now()
  return Number.isFinite(remainingMs) ? Math.max(0, Math.floor(remainingMs / 60000)) : 0
}

/**
 * Copy-to-clipboard, following the pattern `MessageBubble.tsx` already uses in
 * this folder: `navigator.clipboard` plus a sonner toast either way.
 *
 * There IS a shared `CopyButton` (`VulnerabilityClassification.tsx:24`), and it
 * was not reused. It lives in the vulnerabilities PAGE tree, so importing it
 * from `_metronic/partials` inverts the layering, and it would pull that whole
 * module — react-bootstrap cards, the CVSS parser, the EPSS helpers — into the
 * chat bundle for a 20-line button. What is reused instead is the behaviour and
 * the `Copy`/`Copied` message ids, so the affordance still reads the same.
 *
 * `label` names the target ("Copy public key") rather than the action, because
 * a session card has two of these and "Copy, Copy" in a screen reader's button
 * list is a coin toss.
 */
const CopyAction: FC<{ value: string; label: string }> = ({ value, label }) => {
  const intl = useIntl()
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value)
      toast.info(intl.formatMessage({ id: 'Copied' }))
    } catch {
      toast.error(intl.formatMessage({ id: "Couldn't copy to clipboard" }))
    }
  }
  return (
    <button
      type='button'
      className='lum-patch-copy'
      onClick={handleCopy}
      aria-label={label}
      title={label}
    >
      <Copy size={13} aria-hidden='true' />
      <span>{intl.formatMessage({ id: 'Copy' })}</span>
    </button>
  )
}

/**
 * Remaining time, worded relative to now rather than as a raw timestamp — this
 * sits inline on a status line, not a page a user reads deliberately.
 */
const ExpiryNote: FC<{ expiresAt: string }> = ({ expiresAt }) => {
  const intl = useIntl()
  const minutes = minutesLeft(expiresAt)
  return (
    <span className='lum-patch-expiry'>
      {minutes > 0
        ? intl.formatMessage({ id: 'CHAT.PATCH.EXPIRES_IN' }, { minutes })
        : intl.formatMessage({ id: 'CHAT.PATCH.EXPIRED_SOON' })}
    </span>
  )
}

interface RowProps {
  session: PatchSession
  view: PatchSessionView
  /** True when this session was opened from a chat other than the one open now. */
  isOtherChat: boolean
}

/**
 * One session's worth of read-only status.
 *
 * The agent owns the whole lifecycle: it starts a session, checks its
 * connectivity and ends it, each as an approval-gated tool call. There is no
 * form, no button and nothing to mutate here — the row reports what the
 * server says and, while a session waits on its key, shows the public key and
 * the command to install it, since installing that key on the target host is
 * the one step only a human can do.
 *
 * Status is rendered as ordinary text, not a live region — see
 * `PatchSessionPanel`'s comment on why the region moved to the panel summary.
 */
const PatchSessionRow: FC<RowProps> = ({ session, view, isOtherChat }) => {
  const intl = useIntl()
  const uid = useId()

  const otherChatNote = isOtherChat ? (
    <span className='lum-patch-other-chat'>
      {intl.formatMessage({ id: 'CHAT.PATCH.OTHER_CHAT' })}
    </span>
  ) : null

  /**
   * `ReactElement`, not `ReactNode`: the non-pending branch below returns this
   * as the row's whole output, and `ReactNode` admits `undefined` and bare
   * strings, which `FC` does not. Every branch already returns an element; the
   * `never` one is assignable to anything, so this narrows the annotation
   * without changing any behaviour.
   */
  const statusHeader = (): ReactElement => {
    switch (view) {
      case 'connected':
        // A host comes from scanned asset data, so it gets the same bidi
        // treatment as model-generated text — see `neutraliseBidi`'s comment.
        return (
          <div className='lum-patch-banner'>
            <span className='lum-patch-dot' aria-hidden='true' />
            <ShieldAlert size={16} aria-hidden='true' />
            <span className='lum-patch-banner-label'>
              {intl.formatMessage(
                { id: 'CHAT.PATCH.SESSION_ACTIVE_TARGET' },
                {
                  target: (
                    <code dir='ltr'>
                      {neutraliseBidi(`${session.target_username}@${session.target_host}`)}
                    </code>
                  ),
                },
              )}
            </span>
            {otherChatNote}
            <ExpiryNote expiresAt={session.expires_at} />
          </div>
        )

      case 'terminal':
        // A fact about the past — no live region, no target, no key. Opening
        // an old chat must not announce a fresh ending or invite acting on a
        // session that is already gone.
        return (
          <div className='lum-patch-banner lum-patch-banner--terminal'>
            <ShieldAlert size={16} aria-hidden='true' />
            <span className='lum-patch-banner-label'>
              {intl.formatMessage({ id: 'Patch session ended — start a new one' })}
            </span>
          </div>
        )

      case 'pending':
        return (
          <div className='lum-patch-status-row'>
            <span className='lum-patch-pending-label'>
              {intl.formatMessage({ id: 'Awaiting authorization' })}
            </span>
            {otherChatNote}
            <ExpiryNote expiresAt={session.expires_at} />
          </div>
        )

      default: {
        // Exhaustiveness: adding a member to `PatchSessionView` without a
        // branch here is a compile error, not a blank row at runtime.
        const unhandled: never = view
        return unhandled
      }
    }
  }

  // connected / terminal are the status line and nothing more.
  if (view !== 'pending') return statusHeader()

  // pending_authorization: the key still has to be installed on the host by a
  // human, so the row carries the public key and the exact install command.
  const target = neutraliseBidi(`${session.target_username}@${session.target_host}`)
  // §13:938. The COPIED value is verbatim, because the user pastes it into a
  // shell — a "friendlier" rewrite is a command that does not work. `~user`
  // CANNOT be quoted, so the username necessarily lands unquoted; the agent
  // validated it on start. The DISPLAYED value below is still bidi-neutralised
  // — copying and reading are different trust boundaries, and only the copy
  // has to be byte-identical to what the host expects.
  const installCommand = `echo '${session.public_key}' >> ~${session.target_username}/.ssh/authorized_keys`

  /*
   * `role='group'` is what makes `aria-labelledby` mean anything: on a bare
   * `<div>` with no role the attribute is ignored, and the heading it points at
   * would never be announced with the region. Same pairing as `ApprovalCard`.
   */
  return (
    <div className='lum-patch-setup' role='group' aria-labelledby={`${uid}-key-title`}>
      {statusHeader()}

      <p className='lum-patch-setup-title' id={`${uid}-key-title`}>
        <KeyRound size={15} aria-hidden='true' />{' '}
        {intl.formatMessage(
          { id: 'CHAT.PATCH.KEY_TITLE' },
          { target: <code dir='ltr'>{target}</code> },
        )}
      </p>
      {/*
        §13:926-939. The private half is AES-256-GCM encrypted before it touches
        the database, is never returned by any endpoint, and is wiped on `end`.
        Saying so is not marketing: this row is asking someone to paste a key
        into a production host's `authorized_keys`, and "what exactly am I
        trusting you with" is the reasonable question.
      */}
      <p className='lum-patch-setup-story'>{intl.formatMessage({ id: 'CHAT.PATCH.KEY_STORY' })}</p>

      <div className='lum-patch-field-head'>
        <span className='lum-patch-field-label'>
          {intl.formatMessage({ id: 'CHAT.PATCH.PUBLIC_KEY_LABEL' })}
        </span>
        <CopyAction
          value={session.public_key}
          label={intl.formatMessage({ id: 'CHAT.PATCH.COPY_KEY' })}
        />
      </div>
      <pre className='lum-terminal lum-patch-key' dir='ltr'>
        {session.public_key}
      </pre>

      <div className='lum-patch-field-head'>
        <span className='lum-patch-field-label'>
          {intl.formatMessage({ id: 'CHAT.PATCH.INSTALL_COMMAND_LABEL' })}
        </span>
        <CopyAction
          value={installCommand}
          label={intl.formatMessage({ id: 'CHAT.PATCH.COPY_COMMAND' })}
        />
      </div>
      <code className='lum-terminal lum-patch-command' dir='ltr'>
        {neutraliseBidi(installCommand)}
      </code>
    </div>
  )
}

/**
 * Every live session the caller can reach, read-only.
 *
 * A session opened in one conversation is usable from another — the agent
 * resolves a tool call's `session_id` against the caller's whole listing, not
 * just this chat's — so a panel that showed only "this chat's most recent
 * session" under-reported what the agent could actually act on. This renders
 * one row per live session, newest first (the order `sessions` already
 * arrives in), and marks the ones opened elsewhere.
 *
 * Renders `null` on an empty list: the parent decides whether there is
 * anything here worth mounting a card for at all.
 *
 * The live region lives HERE, not per row. It used to sit on the single row's
 * own status label — correct for one session, but with several, announcing
 * three rows separately on first render is noise a screen-reader user did not
 * ask for. What is worth announcing as a unit is the panel's summary (how many
 * sessions are live); each row's own status is ordinary text underneath it.
 *
 * The region is conditional on `liveCount > 0`, so it stays mounted across
 * transitions BETWEEN one live count and another — a second session starting,
 * or one of several ending while others stay live. It does NOT cover the
 * panel's own 0→1 mount: `sessions.length === 0` returns `null` above it, so
 * the first live session inserts the region and its own announcement in the
 * same render, same as any region that did not exist a moment ago. Nothing
 * exercises that seam today — the caller only mounts this panel once a live
 * session already exists — so it is unaddressed here, not fixed.
 */
const PatchSessionPanel: FC<Props> = ({ sessions, currentChatId }) => {
  const intl = useIntl()

  if (sessions.length === 0) return null

  const rows = sessions.map((session) => ({ session, view: resolvePatchSessionView(session) }))
  // "Live" excludes a defensively-passed terminal row (see `useActivePatchSessions`,
  // which already filters these out) — a fact about the past is not part of
  // the count this summary is announcing.
  const liveCount = rows.filter((row) => row.view !== 'terminal').length

  return (
    <div className='lum-patch-sessions'>
      {liveCount > 0 && (
        <p key={LIVE_REGION_KEY} className='lum-patch-sessions-title' role='status'>
          {intl.formatMessage({ id: 'CHAT.PATCH.SESSIONS_TITLE' }, { count: liveCount })}
        </p>
      )}
      {rows.map(({ session, view }) => (
        <PatchSessionRow
          key={session.id}
          session={session}
          view={view}
          isOtherChat={session.chat_id !== currentChatId}
        />
      ))}
    </div>
  )
}

export default PatchSessionPanel
